import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  apiRequest,
  ApiError,
  ApiRequestOptions,
  isSuccessStatus,
} from "../lib/api-request";

// Thin wrapper around `useQuery`. Pass the request options (endpoint, params,
// etc.) plus standard query options (queryKey, enabled, select, ...). The
// queryFn throws `ApiError` on a non-2xx response so error/retry/isError work.
type UseApiQueryArgs<T> = Pick<
  ApiRequestOptions,
  "endpoint" | "method" | "params" | "pathParam" | "headers"
> &
  Omit<UseQueryOptions<T, ApiError>, "queryFn">;

export function useApiQuery<T = any>(args: UseApiQueryArgs<T>) {
  const { endpoint, method, params, pathParam, headers, ...queryOptions } = args;

  return useQuery<T, ApiError>({
    ...queryOptions,
    queryFn: async ({ signal }) => {
      const { body, status } = await apiRequest<T>({
        endpoint,
        method,
        params,
        pathParam,
        headers,
        signal,
      });
      if (!isSuccessStatus(status)) throw new ApiError(status, body);
      return body;
    },
  });
}
